import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'antd';
import { SubsContext } from './SubscriptionInfo';
import { gainPlan, getPlanName, losePlan, valuePlan } from './plans';
import { isFeatureEnabled } from '../../config/featureFlags';

const PlanConfirmationModal = ({
  visible,
  selectedPlan,
  onConfirm,
  onCancel,
}) => {
  const {
    billsbyCustomerId,
    subscription,
    plans,
    subscriptionLevel,
    freeTrialPeriod,
    previousPlan,
  } = useContext(SubsContext);

  const currSubscriptionLevel = subscription ? subscription.planName.split(' - ')[1] : subscriptionLevel;
  const nextSubscriptionLevel = subscription && subscription.planIdNext ? getPlanName(subscription.planIdNext, plans).split(' - ')[1] : currSubscriptionLevel;
  const inTrial = subscription && subscription.status === 'InTrial';
  const actualSubscriptionLevel = !billsbyCustomerId || inTrial ? 'ICON Pro' : currSubscriptionLevel;

  if (!selectedPlan || !plans) {
    return null;
  }

  return (
    <Modal
      width={700}
      title="Confirm Subscription"
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onConfirm}
        >
          Continue to Payment
        </Button>,
      ]}
    >
      <div>
        {selectedPlan.previousPlan && (
          <h2>Confirm Cancellation</h2>
        )}
        {!selectedPlan.previousPlan && (<span>
          {previousPlan && (
            <h2>Confirm Reinstatement of {selectedPlan.name}</h2>
          )}
          {!previousPlan && (
            <h2>Confirm Subscription {billsbyCustomerId ? ' Change' : ''} to {selectedPlan.name}</h2>
          )}
        </span>)}
        {!billsbyCustomerId && (<p>
          {isFeatureEnabled('ENABLE_FREE_TRIAL') && (<span>Upon acceptance of your credit card, you will begin a {freeTrialPeriod} <b>Free Trial</b> period, during which you will
            have <b>ICON Pro</b> access to all the features of ICON.  At the end of the trial period,&nbsp;</span>)}
          {!isFeatureEnabled('ENABLE_FREE_TRIAL') && (<span>Upon acceptance of your credit card, </span>)}
          {selectedPlan.name !== 'ICON Pro' && (<span>
            you will switch to <b>{selectedPlan.name}</b>, and&nbsp;
          </span>)}
          {selectedPlan.name === 'ICON Pro' && (<span>
            you will&nbsp;
          </span>)}
          {selectedPlan.price === 'FREE' && (<span>
            be charged nothing.
          </span>)}
          {selectedPlan.price !== 'FREE' && (<span>
            be charged <b>{selectedPlan.price}</b>.
          </span>)}
        </p>)}
        {billsbyCustomerId && (
          <div>
            {inTrial && isFeatureEnabled('ENABLE_FREE_TRIAL') && (<p>
              You are still in your {freeTrialPeriod} <b>Free Trial</b> period and will have <b>ICON Pro</b> access until the first billing cycle, after which time&nbsp;
              {selectedPlan.name !== 'ICON Pro' && (<span>
                you will switch to <b>{selectedPlan.name}</b>, and&nbsp;
              </span>)}
              {selectedPlan.name === 'ICON Pro' && (<span>
                you will&nbsp;
              </span>)}
              {selectedPlan.price === 'FREE' && (<span>
                be charged nothing.
              </span>)}
              {selectedPlan.price !== 'FREE' && (<span>
                be charged <b>{selectedPlan.price}</b>.
              </span>)}
            </p>)}
            {!inTrial && (
              <div>
                {(valuePlan(selectedPlan.name) < valuePlan(currSubscriptionLevel) || (inTrial && !isFeatureEnabled('ENABLE_FREE_TRIAL'))) && (<div>
                  {selectedPlan.price === 'FREE' && (<p>
                    You are switching from <b>{currSubscriptionLevel}</b> to <b>{selectedPlan.name}</b>
                    {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of ${nextSubscriptionLevel})` : ''},
                    which is free, beginning at the next billing cycle.  Your credit
                    card will remain on file, but will not be charged.
                  </p>)}
                  {selectedPlan.price !== 'FREE' && (<p>
                    You are switching from <b>{currSubscriptionLevel}</b> to <b>{selectedPlan.name}</b>
                    {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of ${nextSubscriptionLevel})` : ''},
                    beginning at the next billing cycle, at which time
                    you will be charged <b>{selectedPlan.price}</b>.
                  </p>)}
                </div>)}
                {valuePlan(selectedPlan.name) > valuePlan(currSubscriptionLevel) && (
                  <p>
                    You will be switching from <b>{currSubscriptionLevel}</b> to <b>{selectedPlan.name}</b>
                    {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of ${nextSubscriptionLevel})` : ''},
                    which is <b>{selectedPlan.price}</b> as soon as you confirm.
                    You will be charged for the pro-rated difference in price.
                  </p>
                )}
                {valuePlan(selectedPlan.name) === valuePlan(currSubscriptionLevel) && (
                  <p>
                    You will be remaining at <b>{selectedPlan.name}</b>
                    {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of switching to ${nextSubscriptionLevel})` : ''},
                    beginning at the next billing cycle, and
                    you will still be charged <b>{selectedPlan.price}</b>.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {valuePlan(actualSubscriptionLevel) > valuePlan(selectedPlan.name) && (<div>
          You will then lose the following features:
          <ul>
            {losePlan(actualSubscriptionLevel, selectedPlan.name).map((item, index) => (
              <li key={`lose-${index}`}>{item}</li>
            ))}
          </ul>
        </div>)}
        {valuePlan(actualSubscriptionLevel) < valuePlan(selectedPlan.name) && (<div>
          You will gain the following features:
          <ul>
            {gainPlan(actualSubscriptionLevel, selectedPlan.name).map((item, index) => (
              <li key={`gain-${index}`}>{item}</li>
            ))}
          </ul>
        </div>)}
      </div>
    </Modal>
  );
};

PlanConfirmationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  selectedPlan: PropTypes.shape({
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    userTypeName: PropTypes.string.isRequired,
    previousPlan: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

PlanConfirmationModal.defaultProps = {
  selectedPlan: null,
};

export default PlanConfirmationModal;




// WEBPACK FOOTER //
// ./src/components/Billsby/PlanConfirmationModal.jsx